import * as React from 'react';
import { Card, Select, DatePicker, Spin } from 'antd';
import { Link } from 'react-router-dom';
import '../css/Dashboard.scss';
import BarChart from './chart/BarChart';
import ActivityFeed from './activityFeed/ActivityFeed';
// import strings from '../eyeguide-localization/employee/strings.employee';
import Service from '../service/apolloclient.service';
import LocalStorage from '../service/localstorage';
import moment from 'moment';

const ser = new Service();
const keys: any = ['tests', 'orgs', 'logins', 'syncs'];
const { RangePicker } = DatePicker;
const storage = new LocalStorage();

interface AdminDashboardProps {
    location: any;
}

interface AdminDashboardState {
    dateRange: any;
    switch: any;
    counts: any;
    metric: any;
    orgs: any;
    tests: any;
    syncs: any;
    logins: any;
    duration: number;
    time: number;
    loading: boolean;
}

export default class AdminDashboard
    extends React.Component<AdminDashboardProps, AdminDashboardState> {
    constructor(props: any) {
        super(props);
        this.state = {
            switch: '',
            counts: {
                tests: 0,
                logins: 0,
                syncs: 0,
                orgs: 0
            },
            dateRange: ['', ''],
            metric: undefined,
            duration: 0,
            time: Date.now(),
            orgs: {},
            tests: {},
            syncs: {},
            logins: {},
            loading: false
        };
        window.onbeforeunload = () => {
            let state = JSON.stringify({
                dateRange: this.state.dateRange,
                metric: this.state.metric
            });
            storage.setDateRange(state);
        };
    }

    /**
     * Check if previous state exists in local storage
     * Else load dashboard with default values
     */
    componentDidMount() {
        let state: any = storage.getDateRange();
        let dateRange, metric;
        if (state.length === 0) {
            let date1 = new Date();
            let date2 = new Date();
            date1.setDate(date2.getDate() - 29);
            dateRange = [
                date1.toISOString().split('T')[0],
                date2.toISOString().split('T')[0]
            ];
            metric = '30 days';
        } else {
            dateRange = JSON.parse(state).dateRange;
            metric = JSON.parse(state).metric;
        }
        this.setState(
            { loading: true, dateRange, metric },
            () => this.loadDashboard()
        );
    }

    /**
     * Save current state in local storage to retain it throughout the session
     * It will be cleared on logout
     */
    componentWillUnmount() {
        let state = JSON.stringify({
            dateRange: this.state.dateRange,
            metric: this.state.metric
        });
        console.log('unmount', state);
        storage.setDateRange(state);
    }

    renderCharts = () => {
        let handlers = {
            handleMetricChange: this.handleMetricChange,
            loadNextContent: this.loadNextChart,
            duration: this.state.duration,
            dateRange: this.state.dateRange,
            time: this.state.time
        };
        return (
            <React.Fragment>
                <div className="charts">
                    <BarChart
                        data={this.state.tests.data}
                        index={0}
                        title="Tests"
                        type="stacked"
                        xlabels={this.state.tests.labels}
                        colors={['#1890ff', '#74bcff']}
                        legend={['Baselines', 'Tests']}
                        metric={this.state.tests.metric}
                        {...handlers}
                    />
                    <BarChart
                        data={this.state.orgs.data}
                        index={1}
                        title="Organizations"
                        type="grouped"
                        xlabels={this.state.orgs.labels}
                        colors={['#1890ff', '#74bcff']}
                        legend={['Customers', 'Organizations']}
                        metric={this.state.orgs.metric}
                        {...handlers}
                    />
                </div>
                <div className="charts">
                    <BarChart
                        data={this.state.logins.data}
                        index={2}
                        title="Logins"
                        xlabels={this.state.logins.labels}
                        colors={['#1890ff']}
                        legend={[]}
                        metric={this.state.logins.metric}
                        {...handlers}
                    />
                    <BarChart
                        data={this.state.syncs.data}
                        index={3}
                        title="Syncs"
                        xlabels={this.state.syncs.labels}
                        colors={['#1890ff']}
                        legend={[]}
                        metric={this.state.syncs.metric}
                        {...handlers}
                    />
                </div>
            </React.Fragment>
        );
    }

    /**
     * Handle KPI counts on metric change
     * @param value 30/60/120/365 days
     */
    handleKPICounts = (value: any) => {
        let date1 = new Date();
        let date2 = new Date();
        let diff = parseInt(value.split(' ')[0], 10);
        date1.setDate(date2.getDate() - diff);
        this.setState(
            {
                metric: value,
                dateRange: [
                    date1.toISOString().split('T')[0],
                    date2.toISOString().split('T')[0]
                ],
                loading: true
            },
            () => this.loadDashboard()
        );
    }

    handleDateRange = (date: any, dateString: any) => {
        console.log('date range', dateString);
        if (dateString[0] !== '') {
            this.setState(
                {dateRange: dateString, metric: undefined, loading: true},
                () => this.loadDashboard()
            );
        } else {
            this.setState(
                {
                    dateRange: this.getDateRangeByMetric('30 days'),
                    metric: '30 days',
                    loading: true
                },
                () => this.loadDashboard()
            );
        }
    }

    formatCount(num: number) {
        if (num.toString().length >= 7) {
            let num1: string = (num / 1000000).toFixed(1);
            if (parseFloat(num1) % 1 === 0) {
                return num1.split('.')[0] + 'M';
            } else {
                return num1 + 'M';
            }
        } else if (num.toString().length >= 5) {
            return Math.round(num / 1000) + 'K';
        }
        return num;
    }

    renderDashboard = () => {
        const {counts, dateRange} = this.state;
        return (
            <React.Fragment>
                <Card>
                    <RangePicker
                        onChange={this.handleDateRange}
                        format="YYYY-MM-DD"
                        value={dateRange[0] !== '' ?
                            [moment(dateRange[0]), moment(dateRange[1])] : []}
                        disabledDate={(date) => moment().diff(date) < 0}
                    />
                    &nbsp;&nbsp;&nbsp;&nbsp;
                    <Select
                        style={{ width: '250px' }}
                        value={this.state.metric}
                        placeholder="# of Last..."
                        onChange={(e) => this.handleKPICounts(e)}
                    >
                        <Select.Option key="30 days">Last 30 Days</Select.Option>
                        <Select.Option key="60 days">Last 60 Days</Select.Option>
                        <Select.Option key="120 days">Last 120 Days</Select.Option>
                        <Select.Option key="365 days">Last 365 Days</Select.Option>
                    </Select>
                    &nbsp;&nbsp;&nbsp;&nbsp;
                    <Spin spinning={this.state.loading} />
                </Card><br />
                <div
                    className="grid-container"
                    style={{ pointerEvents: this.state.switch.length ? 'none' : 'auto' }}
                >
                    {keys.map((key, index) => {
                        return (
                            counts[key] > 0 ?
                                <Link
                                    to={`/dashboard/${key}/${counts[key]}/${dateRange[0]}/${dateRange[1]}`}
                                    key={key}
                                >
                                    <Card>
                                        <p># of {key}</p>
                                        <p className="count">
                                            {this.formatCount(counts[key])}</p>
                                    </Card>
                                </Link> :
                                <Card>
                                    <p># of {key}</p>
                                    <p className="count">
                                        {this.formatCount(counts[key])}</p>
                                </Card>
                        );
                    })}
                </div>
                <br /><br />
                {this.renderCharts()}
                <ActivityFeed
                    feedSize={60}
                    rosterid="-1"
                    isCustomer={false}
                />
            </React.Fragment>
        );
    }

    render() {
        console.log('location', this.props.location);
        return (
            <React.Fragment>
                {this.props.location.pathname === '/dashboard'
                    && this.renderDashboard()}
            </React.Fragment>
        );
    }

    /**
     * Get chart details from chart data
     * @param chartData object array
     * @param column    chart index
     * @param metric    day/week/month/year
     */
    getChartDetails(chartData: any, column: any, metric: any) {
        let data: any = [];
        let dates: any = [];
        let labels: any = [];
        chartData.forEach((row: any) => {
            switch (column) {
                case 0: data.push([row.baselines, row.tests]);
                        break;
                case 1: data.push([row.customers, row.organizations]);
                        break;
                case 2: data.push([row.logins]);
                        break;
                case 3: data.push([row.syncs]);
                        break;
                default: data.push([0]);
            }
            dates.push(row.recorded_date);
            labels.push(this.getLabelByMetric(row.recorded_date, metric));
        });

        return { data, dates, labels, metric };
    }

    /**
     * Handle chart metric change
     * @param metric day/week/month/year
     */
    handleMetricChange = (metric: any, index: any) => {
        let dateRange = this.state.metric !== undefined
            ? this.getDateRangeByMetric(this.state.metric)
            : this.state.dateRange;
        ser.getChartData(20, { dateRange, metric, order: 'desc', initial: 1 })
            .then((res: any) => {
                let data = res.slice().reverse();
                this.setState({
                    time: Date.now(),
                    [keys[index]]: this.getChartDetails(data, index, metric)
                });
            });
    }

    /**
     * Get display label of a date by metric
     * @param date date string
     * @param metric day/week/month/year
     */
    getLabelByMetric = (date, metric) => {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
            'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        let dateObj = new Date(date);
        let label = '';
        if (metric === 'day') {
            label = months[dateObj.getMonth()] + ' ' + dateObj.getDate();
        } else if (metric === 'week') {
            label = months[dateObj.getMonth()] + ' ' + dateObj.getDate() + ' - ';
            dateObj.setDate(dateObj.getDate() + 6);
            label += months[dateObj.getMonth()] + ' ' + dateObj.getDate();
        } else if (metric === 'month') {
            label = months[dateObj.getMonth()] + ' \'' + dateObj.getFullYear().toString().substring(2, 4);
        } else {
            label = dateObj.getFullYear().toString();
        }
        return label;
    }

    getWeekOfMonth(date: any) {
        let adjustedDate = date.getDate() + date.getDay();
        let prefixes = ['0', '1', '2', '3', '4', '5'];
        return (parseInt(prefixes[0 | adjustedDate / 7], 10) + 1);
    }

    /**
     * Append or prepend next data to prev data of state
     * @param key       tests/orgs/logins/syncs
     * @param nextData  next data of [key]
     * @param order     asc/desc
     * Prepend if order is desc else append
     */
    mergeWithState = (key, nextData, order) => {
        let prevData = Object.assign({}, this.state[key]);
        if (order === 'desc') {
            this.setState({
                [key]: {
                    data: nextData.data.concat(prevData.data),
                    dates: nextData.dates.concat(prevData.dates),
                    labels: nextData.labels.concat(prevData.labels),
                    metric: prevData.metric
                }
            });
        } else {
            this.setState({
                [key]: {
                    data: prevData.data.concat(nextData.data),
                    dates: prevData.dates.concat(nextData.dates),
                    labels: prevData.labels.concat(nextData.labels),
                    metric: prevData.metric
                }
            });
        }
    }

    /**
     * Get last date in UTC format (YYYY-MM-DD)
     * @param date      Date object
     * @param metric    day/week/month/year
     * @param order     asc/desc
     * If order is asc, return 'date after the last date' of that metric
     * Else return date of yesterday
     */
    getLastDate(date: any, metric: string, order: string) {
        if (order === 'asc') {
            switch (metric) {
                case 'year' :   date = new Date(date.getFullYear() + 1, 0, 1);
                                break;
                case 'month':   date = new Date(date.getFullYear(), date.getMonth() + 1, 1);
                                break;
                case 'week' :   date.setDate(date.getDate() + 7);
                                break;
                default     :   date.setDate(date.getDate() + 1);
            }
        } else {
            date.setDate(date.getDate() - 1);
        }
        return date.getFullYear() + '-' + (date.getMonth() + 1) + '-' + date.getDate();
    }

    getUTCDate(date: any) {
        return date.getUTCFullYear() + '-' + (date.getUTCMonth() + 1) + '-' + date.getUTCDate();
    }

    /**
     * Load next/prev chart data on scroll end
     * @param index     chart index
     * @param metric    day/week/month/year
     * @param order     asc/desc
     * If order is asc, load data before the last date
     */
    loadNextChart = (index: number, metric: string, order: string) => {
        // alert('debounce');
        let dates = this.state[keys[index]].dates;
        let lastDate;
        if (order === 'asc') {
            lastDate = new Date(dates[dates.length - 1]);
        } else {
            lastDate = new Date(dates[0]);
        }
        lastDate.setDate(lastDate.getDate());
        let dateRange;
        if (order === 'asc') {
            dateRange = [
                this.getLastDate(lastDate, metric, order),
                this.getUTCDate(new Date(this.state.dateRange[1]))
            ];
        } else {
            dateRange = [
                this.getUTCDate(new Date(this.state.dateRange[0])),
                this.getLastDate(lastDate, metric, order)
            ];
        }
        console.log('date range', dateRange);
        ser.getChartData(20, { dateRange, metric, order, initial: 0 })
            .then((res: any) => {
                let nextData: any = [];
                if (order === 'desc') {
                    nextData = this.getChartDetails(res.slice().reverse(), index, metric);
                } else {
                    nextData = this.getChartDetails(res, index, metric);
                }
                this.mergeWithState(keys[index], nextData, order);
            });
    }

    /**
     * Get date range by metric
     * @param metric 30/60/120/365 days
     */
    getDateRangeByMetric(metric: string) {
        let date1 = new Date();
        let date2 = new Date();
        let diff = parseInt(metric.split(' ')[0], 10);
        date1.setDate(date2.getDate() - diff);
        return [this.getUTCDate(date1), this.getUTCDate(date2)];
    }

    /**
     * If metric is defined, get duration of last metric (30/60/120/365) days 
     * Else get duration between date range of state
     */
    getDuration() {
        if (this.state.metric !== undefined) {
            return parseInt(this.state.metric.split(' ')[0], 10);
        } else {
            const date1: any = new Date(this.state.dateRange[0]);
            const date2: any = new Date(this.state.dateRange[1]);
            const diffTime = Math.abs(date2 - date1);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            console.log('----duration----', diffDays);
            return diffDays;
        }
    }

    /**
     * Load dashboard data on mount or period change
     */
    async loadDashboard() {
        let dateRange = this.state.dateRange;
        let counts: any = await ser.getActivityCounts({ dateRange });
        let metric = 'day';
        let duration = this.getDuration();
        if (duration >= 120) {
            metric = 'week';
        }
        let res: any = await ser.getChartData(
            20, { dateRange, metric, order: 'desc', initial: 1 }
        );
        let data = res.slice().reverse();
        this.setState({
            counts,
            duration,
            time: Date.now(),
            tests: this.getChartDetails(data, 0, metric),
            orgs: this.getChartDetails(data, 1, metric),
            logins: this.getChartDetails(data, 2, metric),
            syncs: this.getChartDetails(data, 3, metric),
            loading: false
        });
    }
}


// WEBPACK FOOTER //
// ./src/components/AdminDashboard.tsx